import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography, Avatar, useTheme } from '@mui/material';
import { Person, PersonAdd } from '@mui/icons-material';

interface Client {
    id: string;
    name: string;
    email: string;
    phone: string;
    business?: {
        name: string;
    };
}

interface ClientAutocompleteProps {
    value: Client | null;
    onChange: (client: Client | null | string) => void;
    onClientSelect: (client: Client) => void;
    label?: string;
    error?: boolean;
    helperText?: string;
}

const ClientAutocomplete: React.FC<ClientAutocompleteProps> = ({
    value,
    onChange,
    onClientSelect,
    label = "Buscar Cliente",
    error,
    helperText
}) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<Client[]>([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    useEffect(() => {
        if (value) {
            setInputValue(value.name);
        } else {
            setInputValue('');
        }
    }, [value]);

    useEffect(() => {
        let active = true;

        if (!open) {
            return undefined;
        }

        (async () => {
            setLoading(true);
            try {
                // In a real app we might want to debounce this or only search on input
                const API_URL = process.env.REACT_APP_API_URL || 'https://api.turn.ar';
                const response = await fetch(`${API_URL}/api/clients`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                const data = await response.json();
                if (active) {
                    setOptions(data);
                }
            } catch (error) {
                console.error('Error fetching clients:', error);
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            active = false;
        };
    }, [open]);

    return (
        <Autocomplete
            id="client-autocomplete"
            freeSolo
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            isOptionEqualToValue={(option, value) => {
                // Handle both object and string value comparisons
                const optionName = option.name;
                const valueName = typeof value === 'string' ? value : value?.name;
                return optionName === valueName;
            }}
            getOptionLabel={(option) => {
                if (typeof option === 'string') {
                    return option;
                }
                return option.name;
            }}
            options={options}
            loading={loading}
            value={value}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
                // Propagate changes immediately as a string to allow creating new clients
                // This ensures "new client" validation passes in parent
                onChange(newInputValue);
            }}
            onChange={(event, newValue) => {
                if (typeof newValue === 'string') {
                    onChange(newValue);
                } else if (newValue) {
                    // It's a client object
                    onChange(newValue);
                    onClientSelect(newValue);
                } else {
                    onChange(null);
                }
            }}
            renderOption={(props, option) => (
                <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: isDark ? '#2A2040' : '#E8D5F2', color: '#9B59B6' }}>
                            <Person fontSize="small" />
                        </Avatar>
                        <Box>
                            <Typography variant="body1" fontWeight="bold">
                                {option.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {option.email} {option.phone ? `• ${option.phone}` : ''}
                                {option.business ? ` • ${option.business.name}` : ''}
                            </Typography>
                        </Box>
                    </Box>
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    size="small"
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            noOptionsText={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                    <PersonAdd fontSize="small" />
                    <Typography variant="body2">
                        Presiona Enter para crear "{inputValue}"
                    </Typography>
                </Box>
            }
        />
    );
};

export default ClientAutocomplete;
